import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { 
  Search, Filter, ChevronDown, Clock, Award, Users, 
  BookOpen, Shield, Briefcase, AlertTriangle, UserCheck, Ban,
  GraduationCap, Building2, CheckCircle2, ArrowRight, ExternalLink,
  Mail, Globe, TrendingUp, Sparkles, X, Compass, Cpu, Scale,
  FileCheck, Brain, Lock, Eye, Heart, Laptop
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import CourseFinderWizard from '../components/CourseFinderWizard';
import CourseDetailModal from '../components/CourseDetailModal';
const API = API_BASE;

const ACADEMY_LOGO = "https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/z5p2uktj_Navigate%20learning%20academy.png";

// Specialisation icons and colors
const SPECIALISATIONS = [
  { id: 'anti-money-laundering', name: 'Anti-Money Laundering', icon: Shield, color: 'from-blue-500 to-blue-600' },
  { id: 'grc', name: 'GRC', icon: Briefcase, color: 'from-[#1EBFB3] to-[#1EBFB3]' },
  { id: 'anti-financial-crime', name: 'Anti-Financial Crime', icon: AlertTriangle, color: 'from-red-500 to-red-600' },
  { id: 'fraud', name: 'Fraud', icon: Eye, color: 'from-orange-500 to-orange-600' },
  { id: 'compliance', name: 'Compliance', icon: FileCheck, color: 'from-green-500 to-green-600' },
  { id: 'artificial-intelligence', name: 'Artificial Intelligence', icon: Brain, color: 'from-cyan-500 to-cyan-600' },
  { id: 'cybersecurity', name: 'Cybersecurity', icon: Lock, color: 'from-slate-500 to-slate-600' },
  { id: 'data-privacy', name: 'Data Privacy', icon: Eye, color: 'from-violet-500 to-violet-600' },
  { id: 'it-governance', name: 'IT Governance', icon: Laptop, color: 'from-indigo-500 to-indigo-600' },
  { id: 'conduct', name: 'Conduct', icon: Heart, color: 'from-pink-500 to-pink-600' },
  { id: 'anti-corruption', name: 'Anti-Corruption', icon: Scale, color: 'from-amber-500 to-amber-600' },
  { id: 'operations', name: 'Operations', icon: Briefcase, color: 'from-gray-500 to-gray-600' },
  { id: 'risk-management', name: 'Risk Management', icon: AlertTriangle, color: 'from-yellow-500 to-yellow-600' },
  { id: 'social-impact-compliance', name: 'Social Impact Compliance', icon: Users, color: 'from-teal-500 to-teal-600' },
];

// Categories (qualification levels)
const CATEGORIES = [
  { id: 'short-course', name: 'Short Course', qaa: null },
  { id: 'certificate', name: 'Certificate', qaa: 'UK QAA Level 2' },
  { id: 'specialist', name: 'Specialist Certificate', qaa: 'UK QAA Level 2' },
  { id: 'advanced', name: 'Advanced Certificate', qaa: 'UK QAA Level 4' },
  { id: 'diploma', name: 'Diploma', qaa: 'UK QAA Level 6' },
  { id: 'postgrad', name: 'Postgraduate Diploma', qaa: 'UK QAA Level 8' },
  { id: 'practitioner', name: 'Certified Practitioner', qaa: null },
  { id: 'occupational', name: 'Occupational Certificate', qaa: 'NQF Level 5-8' },
];

// Provider logos and thumbnails
const PROVIDERS = {
  ICA: {
    name: 'International Compliance Association',
    logo: 'ICA',
    color: 'bg-blue-600',
    thumbnail: 'https://static.prod-images.emergentagent.com/jobs/01613b58-ef22-473f-bb14-20b0d5effe85/images/78f470c25e467d0f0798f425b5c775f2e412ba950616d5b013e009898b7ddc21.png'
  },
  ICITP: {
    name: 'IC Institute of Technology Professionals',
    logo: 'ICITP',
    color: 'bg-emerald-600',
    thumbnail: 'https://static.prod-images.emergentagent.com/jobs/01613b58-ef22-473f-bb14-20b0d5effe85/images/2d063b58e2f28c2c75a1e094bdd1e4b60f0e3ea28f3bbb458359ff1f4b254fc9.png'
  }
};

const LEVEL_COLORS = {
  essential: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  certificate: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  specialist: 'bg-[#1EBFB3]/20 text-[#1EBFB3] border-[#1EBFB3]/30',
  advanced: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  diploma: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  practitioner: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  postgrad: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  'short-course': 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  occupational: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
};

const LEVEL_LABELS = {
  essential: 'Essential',
  certificate: 'Certificate',
  specialist: 'Specialist Certificate',
  advanced: 'Advanced Certificate',
  diploma: 'Diploma',
  practitioner: 'Certified Practitioner',
  postgrad: 'Postgraduate Diploma',
  'short-course': 'Short Course',
  occupational: 'Occupational Certificate',
};

function formatCurrency(amount, currency = 'ZAR') {
  if (!amount) return 'Contact for pricing';
  if (currency === 'GBP') {
    return `£${amount.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
  return `R${amount.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export default function AcademyPage() {
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialisation, setSelectedSpecialisation] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    company: '',
    inquiry_type: 'individual',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showCourseDetail, setShowCourseDetail] = useState(false);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    filterCourses();
  }, [courses, searchTerm, selectedSpecialisation, selectedCategory, selectedProvider]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/api/ica/courses`);
      setCourses(response.data.courses || []);
      setExchangeRate(response.data.exchange_rate);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const filterCourses = () => {
    let filtered = [...courses];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(c => 
        c.title?.toLowerCase().includes(search) ||
        c.description?.toLowerCase().includes(search) ||
        c.specialisation?.toLowerCase().includes(search)
      );
    }

    if (selectedSpecialisation !== 'all') {
      filtered = filtered.filter(c => c.specialisation_id === selectedSpecialisation);
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(c => c.level === selectedCategory);
    }

    if (selectedProvider !== 'all') {
      filtered = filtered.filter(c => c.provider === selectedProvider);
    }

    setFilteredCourses(filtered);
  };

  const handleInquiry = async (e) => {
    e.preventDefault();
    if (!inquiryForm.full_name || !inquiryForm.email) {
      toast.error('Please fill in your name and email');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/ica/inquiries`, {
        ...inquiryForm,
        course_id: selectedCourse?.course_id,
        course_title: selectedCourse?.title
      });
      toast.success('Inquiry submitted! We will contact you shortly.');
      setShowInquiryModal(false);
      setInquiryForm({
        full_name: '',
        email: '',
        phone: '',
        company: '',
        inquiry_type: 'individual',
        message: ''
      });
    } catch (error) {
      console.error('Failed to submit inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWizardCourseSelect = (course) => {
    setSelectedCourse(course);
    setShowWizard(false);
    setShowCourseDetail(true);
  };

  const handleEnquireFromDetail = (course) => {
    setShowCourseDetail(false);
    setSelectedCourse(course);
    setShowInquiryModal(true);
  };

  // JSON-LD structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Navigate Learning Academy",
    "description": "QCTO accredited professional training provider offering ICA and ICITP qualifications in Compliance, Anti-Money Laundering, GRC, Financial Crime Prevention, AI, and Cybersecurity.",
    "url": "https://cerebro.navigatecompliance.io/academy",
    "logo": ACADEMY_LOGO,
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "ZA"
    }
  };

  return (
    <>
      <Helmet>
        <title>Navigate Academy | Professional Qualifications & Certifications</title>
        <meta name="description" content="Navigate Academy offers QCTO accredited professional qualifications from ICA and ICITP. Study Anti-Money Laundering, GRC, Compliance, AI, Cybersecurity and more. Get 40% off ICA courses." />
        <meta name="keywords" content="ICA courses, ICITP certifications, compliance training, anti money laundering, AML training, GRC courses, AI compliance, cybersecurity, QCTO accredited, South Africa" />
        <meta property="og:title" content="Navigate Academy | Professional Qualifications" />
        <meta property="og:description" content="QCTO accredited ICA and ICITP qualifications with up to 40% discount. Expert-led training in AML, GRC, AI, Cybersecurity." />
        <meta property="og:image" content={ACADEMY_LOGO} />
        <link rel="canonical" href="https://cerebro.navigatecompliance.io/academy" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        {/* Header */}
        <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-20">
              <Link to="/academy" className="flex items-center gap-3">
                <img 
                  src={ACADEMY_LOGO} 
                  alt="Navigate Learning Academy" 
                  className="h-14 w-auto"
                />
              </Link>
              <nav className="hidden md:flex items-center gap-8">
                <a href="#about" className="text-slate-300 hover:text-white transition-colors">About</a>
                <a href="#specialisations" className="text-slate-300 hover:text-white transition-colors">Specialisations</a>
                <a href="#courses" className="text-slate-300 hover:text-white transition-colors">Courses</a>
                <a href="#contact" className="text-slate-300 hover:text-white transition-colors">Contact</a>
              </nav>
              <div className="flex items-center gap-4">
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-300 hover:text-white">
                    Sign In
                  </Button>
                </Link>
                <a href="#courses">
                  <Button className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
                    Explore Courses
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section - About the Academy */}
        <section id="about" className="relative py-20 lg:py-28 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm font-medium mb-6">
                  <Award className="h-4 w-4" />
                  QCTO Accredited Training Provider
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Navigate
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600"> Academy</span>
                </h1>
                <p className="text-xl text-slate-300 mb-6 leading-relaxed">
                  Your gateway to <strong className="text-white">professional qualifications</strong> and <strong className="text-white">internationally recognized certifications</strong> in compliance, governance, risk management, and emerging technologies.
                </p>
                <p className="text-slate-400 mb-8">
                  We partner with world-class awarding bodies including the <strong className="text-slate-300">International Compliance Association (ICA)</strong> and <strong className="text-slate-300">ICITP</strong> to bring you qualifications that advance careers and protect organizations.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg" 
                    onClick={() => setShowWizard(true)}
                    className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-8 h-14 text-lg"
                    data-testid="course-finder-btn"
                  >
                    <Compass className="mr-2 h-5 w-5" />
                    Find My Qualification
                  </Button>
                  <a href="#specialisations">
                    <Button size="lg" variant="outline" className="border-slate-700 text-white hover:bg-slate-800 px-8 h-14 text-lg">
                      Explore by Discipline
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </a>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="grid grid-cols-2 gap-4">
                  {/* Key differentiators */}
                  <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700/50">
                    <GraduationCap className="h-10 w-10 text-amber-500 mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">Qualifications</h3>
                    <p className="text-sm text-slate-400">Lifetime credentials that never expire</p>
                  </div>
                  <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700/50">
                    <Globe className="h-10 w-10 text-blue-500 mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">UK-Aligned</h3>
                    <p className="text-sm text-slate-400">QAA Level 2-8 qualifications</p>
                  </div>
                  <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700/50">
                    <TrendingUp className="h-10 w-10 text-emerald-500 mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">40% Savings</h3>
                    <p className="text-sm text-slate-400">Exclusive ICA partner pricing</p>
                  </div>
                  <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700/50">
                    <Building2 className="h-10 w-10 text-[#1EBFB3] mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">In-House Training</h3>
                    <p className="text-sm text-slate-400">Corporate facilitation available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Specialisations Section */}
        <section id="specialisations" className="py-20 bg-slate-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Explore by Specialisation</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Choose your area of expertise and discover qualifications tailored to your career goals.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {SPECIALISATIONS.map((spec) => {
                const Icon = spec.icon;
                const courseCount = courses.filter(c => c.specialisation_id === spec.id).length;
                return (
                  <button
                    key={spec.id}
                    onClick={() => {
                      setSelectedSpecialisation(spec.id);
                      document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="group p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-amber-500/30 transition-all duration-300 text-left"
                    data-testid={`spec-${spec.id}`}
                  >
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${spec.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-sm font-medium text-white mb-1 group-hover:text-amber-400 transition-colors">
                      {spec.name}
                    </h3>
                    <p className="text-xs text-slate-500">
                      {courseCount} {courseCount === 1 ? 'course' : 'courses'}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Course Catalog Section */}
        <section id="courses" className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Professional Qualifications Catalog</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Browse qualifications from ICA and ICITP. All credentials are lifetime qualifications that never expire.
              </p>
              {exchangeRate && (
                <p className="text-xs text-slate-500 mt-2">
                  Exchange rate: £1 = R{exchangeRate.gbp_zar?.toFixed(2)} (Updated: {new Date(exchangeRate.updated_at).toLocaleDateString()})
                </p>
              )}
            </div>

            {/* Course Finder CTA */}
            <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border border-amber-500/20">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-amber-500/20">
                    <Compass className="h-8 w-8 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Not sure which qualification is right for you?</h3>
                    <p className="text-sm text-slate-400">Answer a few questions and get personalized recommendations</p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowWizard(true)}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold whitespace-nowrap"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Find My Qualification
                </Button>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="mb-8 space-y-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                  <Input
                    placeholder="Search qualifications..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 h-12 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="h-12 border-slate-700 text-white"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                  <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </Button>
              </div>

              {showFilters && (
                <div className="flex flex-wrap gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Specialisation</label>
                    <select
                      value={selectedSpecialisation}
                      onChange={(e) => setSelectedSpecialisation(e.target.value)}
                      className="h-10 px-3 rounded-lg bg-slate-700 border-slate-600 text-white text-sm min-w-[180px]"
                    >
                      <option value="all">All Specialisations</option>
                      {SPECIALISATIONS.map(spec => (
                        <option key={spec.id} value={spec.id}>{spec.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Category</label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="h-10 px-3 rounded-lg bg-slate-700 border-slate-600 text-white text-sm min-w-[180px]"
                    >
                      <option value="all">All Categories</option>
                      {CATEGORIES.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Provider</label>
                    <select
                      value={selectedProvider}
                      onChange={(e) => setSelectedProvider(e.target.value)}
                      className="h-10 px-3 rounded-lg bg-slate-700 border-slate-600 text-white text-sm min-w-[140px]"
                    >
                      <option value="all">All Providers</option>
                      <option value="ICA">ICA</option>
                      <option value="ICITP">ICITP</option>
                    </select>
                  </div>
                  {(selectedSpecialisation !== 'all' || selectedCategory !== 'all' || selectedProvider !== 'all') && (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setSelectedSpecialisation('all');
                        setSelectedCategory('all');
                        setSelectedProvider('all');
                      }}
                      className="h-10 text-slate-400 hover:text-white self-end"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Clear filters
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Results count */}
            <div className="text-sm text-slate-400 mb-6">
              Showing {filteredCourses.length} of {courses.length} qualifications
            </div>

            {/* Course Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1,2,3,4,5,6].map(i => (
                  <div key={i} className="h-96 rounded-2xl bg-slate-800/50 animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredCourses.map(course => {
                  const provider = PROVIDERS[course.provider] || PROVIDERS.ICA;
                  // Use specialisation-specific thumbnail from API, fallback to provider
                  const thumbnail = course.thumbnail || provider.thumbnail;
                  return (
                    <article 
                      key={course.course_id}
                      className="group bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-amber-500/30 transition-all duration-300 overflow-hidden cursor-pointer"
                      onClick={() => {
                        setSelectedCourse(course);
                        setShowCourseDetail(true);
                      }}
                      data-testid={`course-card-${course.course_id}`}
                    >
                      {/* Provider Thumbnail - Compact */}
                      <div className="relative h-24 overflow-hidden">
                        <img 
                          src={thumbnail} 
                          alt={`Delivered by ${course.provider || 'ICA'}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent" />
                        <div className={`absolute top-2 left-2 px-1.5 py-0.5 rounded text-[10px] font-bold text-white ${provider.color}`}>
                          {course.provider || 'ICA'}
                        </div>
                        {course.upcoming_cohorts && course.upcoming_cohorts.length > 0 && (
                          <div className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-amber-500/90 text-[10px] font-medium text-slate-900">
                            {new Date(course.upcoming_cohorts[0].start_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                          </div>
                        )}
                      </div>

                      <div className="p-3">
                        {/* Level Badge & QAA */}
                        <div className="flex items-center justify-between mb-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${LEVEL_COLORS[course.level] || LEVEL_COLORS.certificate}`}>
                            {LEVEL_LABELS[course.level] || course.level}
                          </span>
                          {course.qaa_level && (
                            <span className="text-[10px] text-slate-500">{course.qaa_level}</span>
                          )}
                        </div>

                        {/* Title */}
                        <h3 className="text-sm font-semibold text-white mb-1 group-hover:text-amber-400 transition-colors line-clamp-2 leading-tight">
                          {course.title}
                        </h3>

                        {/* Specialisation */}
                        <div className="flex items-center gap-1 text-xs text-slate-400 mb-2">
                          <BookOpen className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{course.specialisation || course.subject_area}</span>
                        </div>

                        {/* Delivery Info */}
                        <div className="flex items-center gap-2 text-[10px] text-slate-500 mb-2">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {course.duration}
                          </span>
                          <span className={course.cohort_dates ? 'text-amber-400' : 'text-emerald-400'}>
                            {course.cohort_dates ? 'Cohort' : 'Anytime'}
                          </span>
                        </div>

                        {/* Pricing */}
                        <div className="pt-2 border-t border-slate-700/50">
                          <div className="flex items-end justify-between">
                            <div>
                              <div className="text-base font-bold text-white">
                                {formatCurrency(course.pricing?.total_zar)}
                              </div>
                            </div>
                            {course.pricing?.savings_zar > 0 && (
                              <div className="text-[10px] font-semibold text-emerald-400">
                                Save {formatCurrency(course.pricing?.savings_zar)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actions - Compact */}
                      <div className="px-3 pb-3 flex gap-2">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCourse(course);
                            setShowCourseDetail(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="flex-1 border-slate-600 text-white hover:bg-slate-700 text-[11px] h-7"
                          data-testid={`view-details-${course.course_id}`}
                        >
                          Details
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCourse(course);
                            setShowInquiryModal(true);
                          }}
                          size="sm"
                          className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold text-[11px] h-7"
                          data-testid={`enquire-btn-${course.course_id}`}
                        >
                          Enquire
                        </Button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}

            {filteredCourses.length === 0 && !loading && (
              <div className="text-center py-16">
                <BookOpen className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No qualifications found</h3>
                <p className="text-slate-400 mb-4">Try adjusting your search or filters</p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedSpecialisation('all');
                    setSelectedCategory('all');
                    setSelectedProvider('all');
                  }}
                  className="border-slate-700 text-white"
                >
                  Clear all filters
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 bg-slate-900/50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Advance Your Career?</h2>
            <p className="text-slate-400 mb-8">
              Contact our team for personalized qualification recommendations, corporate training inquiries, or enrollment assistance.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8">
              <a href="mailto:hello@navigatelearning.io" className="flex items-center gap-2 text-slate-300 hover:text-amber-400 transition-colors">
                <Mail className="h-5 w-5" />
                hello@navigatelearning.io
              </a>
              <a href="https://navigatelearning.io" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-300 hover:text-amber-400 transition-colors">
                <Globe className="h-5 w-5" />
                navigatelearning.io
              </a>
            </div>
            <Button 
              size="lg" 
              onClick={() => setShowInquiryModal(true)}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-8 h-14 text-lg"
            >
              Get Started Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-slate-800 py-12 bg-slate-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <img src={ACADEMY_LOGO} alt="Navigate Learning Academy" className="h-10" />
              </div>
              <div className="flex items-center gap-6 text-sm text-slate-500">
                <span>Division of Navigate Group</span>
                <span>•</span>
                <span>QCTO Accredited</span>
                <span>•</span>
                <span>ICA Regional Partner</span>
              </div>
              <div className="text-sm text-slate-500">
                © {new Date().getFullYear()} Navigate Learning. All rights reserved.
              </div>
            </div>
          </div>
        </footer>

        {/* Inquiry Modal */}
        {showInquiryModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 rounded-2xl max-w-md w-full border border-slate-700 shadow-2xl">
              <div className="p-6 border-b border-slate-800">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Qualification Inquiry</h3>
                  <button
                    onClick={() => setShowInquiryModal(false)}
                    className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-slate-400" />
                  </button>
                </div>
                {selectedCourse && (
                  <p className="text-sm text-amber-400 mt-2">{selectedCourse.title}</p>
                )}
              </div>
              <form onSubmit={handleInquiry} className="p-6 space-y-4">
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Full Name *</label>
                  <Input
                    value={inquiryForm.full_name}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, full_name: e.target.value }))}
                    placeholder="Your full name"
                    className="bg-slate-800 border-slate-700 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Email *</label>
                  <Input
                    type="email"
                    value={inquiryForm.email}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com"
                    className="bg-slate-800 border-slate-700 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Phone</label>
                  <Input
                    value={inquiryForm.phone}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+27 XX XXX XXXX"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Company/Organization</label>
                  <Input
                    value={inquiryForm.company}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="Your company name"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Inquiry Type</label>
                  <select
                    value={inquiryForm.inquiry_type}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, inquiry_type: e.target.value }))}
                    className="w-full h-10 px-3 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
                  >
                    <option value="individual">Individual Enrollment</option>
                    <option value="corporate">Corporate / Team Training</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Message</label>
                  <textarea
                    value={inquiryForm.message}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Any questions or specific requirements..."
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm resize-none"
                    rows={3}
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold h-12"
                >
                  {submitting ? 'Submitting...' : 'Submit Inquiry'}
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* Course Finder Wizard Modal */}
        {showWizard && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-700 shadow-2xl">
              <div className="p-6 border-b border-slate-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/20">
                      <Compass className="h-6 w-6 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white">Qualification Finder</h3>
                      <p className="text-sm text-slate-400">Find your perfect qualification</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowWizard(false)}
                    className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-slate-400" />
                  </button>
                </div>
              </div>
              <div className="p-6">
                <CourseFinderWizard 
                  courses={courses}
                  onSelectCourse={handleWizardCourseSelect}
                  onClose={() => setShowWizard(false)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Course Detail Modal */}
        {showCourseDetail && selectedCourse && (
          <CourseDetailModal
            course={selectedCourse}
            onClose={() => {
              setShowCourseDetail(false);
              setSelectedCourse(null);
            }}
            onEnquire={handleEnquireFromDetail}
          />
        )}
      </div>
    </>
  );
}
